import React, { useState, useEffect } from "react";
import { useMutation, gql } from "@apollo/client";
import {
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Divider,
  FormControl,
  FormControlLabel,
  Grid,
  IconButton,
  InputLabel,
  MenuItem,
  Select,
  Slider,
  Switch,
  TextField,
  Typography,
} from "@mui/material";
import {
  Save as SaveIcon,
  Add as AddIcon,
  Delete as DeleteIcon,
} from "@mui/icons-material";

const UPDATE_ESCALATION_CONFIG = gql`
  mutation UpdateEscalationConfig(
    $widgetId: ID!
    $escalationConfig: EscalationConfigInput!
  ) {
    updateEscalationConfig(
      widgetId: $widgetId
      escalationConfig: $escalationConfig
    ) {
      escalationConfig {
        escalateOnNoMatch
        escalateAfterTurns
        escalationMessage
        handoffTriggerKeywords
        autoHandoffOnNegativeSentiment
        sentimentThreshold
      }
    }
  }
`;

const DEFAULT_KEYWORDS = [
  "speak to agent",
  "talk to human",
  "live agent",
  "real person",
  "customer service",
  "representative",
];

function EscalationConfigPanel({
  widgetId,
  botSettings,
  loading,
  onRefetch,
  showAlert,
}) {
  const [formData, setFormData] = useState({
    escalateOnNoMatch: false,
    escalateAfterTurns: 5,
    escalationMessage:
      "Let me connect you with a human agent who can better assist you.",
    handoffTriggerKeywords: DEFAULT_KEYWORDS,
    autoHandoffOnNegativeSentiment: true,
    sentimentThreshold: -0.5,
  });
  const [newKeyword, setNewKeyword] = useState("");
  const [hasChanges, setHasChanges] = useState(false);

  const [updateConfig, { loading: saving }] = useMutation(
    UPDATE_ESCALATION_CONFIG,
    {
      onCompleted: () => {
        showAlert("Escalation settings saved successfully", "success");
        setHasChanges(false);
        onRefetch?.();
      },
      onError: (error) => {
        showAlert(`Error saving settings: ${error.message}`, "error");
      },
    }
  );

  // Initialize form data from props
  useEffect(() => {
    if (botSettings?.escalationConfig) {
      setFormData({
        escalateOnNoMatch:
          botSettings.escalationConfig.escalateOnNoMatch ?? false,
        escalateAfterTurns:
          botSettings.escalationConfig.escalateAfterTurns ?? 5,
        escalationMessage:
          botSettings.escalationConfig.escalationMessage ??
          "Let me connect you with a human agent who can better assist you.",
        handoffTriggerKeywords:
          botSettings.escalationConfig.handoffTriggerKeywords?.length > 0
            ? botSettings.escalationConfig.handoffTriggerKeywords
            : DEFAULT_KEYWORDS,
        autoHandoffOnNegativeSentiment:
          botSettings.escalationConfig.autoHandoffOnNegativeSentiment ?? true,
        sentimentThreshold:
          botSettings.escalationConfig.sentimentThreshold ?? -0.5,
      });
    }
  }, [botSettings]);

  const handleChange = (field) => (event) => {
    const value =
      event.target.type === "checkbox"
        ? event.target.checked
        : event.target.value;
    setFormData((prev) => ({
      ...prev,
      [field]: value,
    }));
    setHasChanges(true);
  };

  const handleSliderChange = (field) => (event, newValue) => {
    setFormData((prev) => ({
      ...prev,
      [field]: newValue,
    }));
    setHasChanges(true);
  };

  const handleAddKeyword = () => {
    if (
      newKeyword.trim() &&
      !formData.handoffTriggerKeywords.includes(newKeyword.trim().toLowerCase())
    ) {
      setFormData((prev) => ({
        ...prev,
        handoffTriggerKeywords: [
          ...prev.handoffTriggerKeywords,
          newKeyword.trim().toLowerCase(),
        ],
      }));
      setNewKeyword("");
      setHasChanges(true);
    }
  };

  const handleRemoveKeyword = (keyword) => {
    setFormData((prev) => ({
      ...prev,
      handoffTriggerKeywords: prev.handoffTriggerKeywords.filter(
        (k) => k !== keyword
      ),
    }));
    setHasChanges(true);
  };

  const handleSave = () => {
    updateConfig({
      variables: {
        widgetId,
        escalationConfig: formData,
      },
    });
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" p={4}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box>
      <Typography variant="h6" gutterBottom>
        Escalation Rules
      </Typography>
      <Typography variant="body2" color="textSecondary" paragraph>
        Configure when and how the bot should hand off conversations to human
        agents.
      </Typography>

      <Grid container spacing={3}>
        {/* Handoff Message */}
        <Grid item xs={12}>
          <Card>
            <CardContent>
              <Typography variant="subtitle1" fontWeight="bold" gutterBottom>
                Escalation Message
              </Typography>
              <Typography variant="body2" color="textSecondary" paragraph>
                Message sent to the customer when transferring to a human agent
              </Typography>
              <TextField
                fullWidth
                multiline
                rows={2}
                value={formData.escalationMessage}
                onChange={handleChange("escalationMessage")}
                placeholder="Let me connect you with a human agent..."
                variant="outlined"
              />
            </CardContent>
          </Card>
        </Grid>

        {/* Trigger Keywords */}
        <Grid item xs={12}>
          <Card>
            <CardContent>
              <Typography variant="subtitle1" fontWeight="bold" gutterBottom>
                Handoff Trigger Keywords
              </Typography>
              <Typography variant="body2" color="textSecondary" paragraph>
                When a customer uses these phrases, the bot will offer to
                connect them with an agent
              </Typography>

              <Box display="flex" gap={1} mb={2}>
                <TextField
                  size="small"
                  value={newKeyword}
                  onChange={(e) => setNewKeyword(e.target.value)}
                  placeholder="Add keyword or phrase"
                  onKeyPress={(e) => e.key === "Enter" && handleAddKeyword()}
                  sx={{ flex: 1 }}
                />
                <Button
                  variant="outlined"
                  startIcon={<AddIcon />}
                  onClick={handleAddKeyword}
                  disabled={!newKeyword.trim()}
                >
                  Add
                </Button>
              </Box>

              <Box display="flex" flexWrap="wrap" gap={1}>
                {formData.handoffTriggerKeywords.map((keyword, index) => (
                  <Chip
                    key={index}
                    label={keyword}
                    onDelete={() => handleRemoveKeyword(keyword)}
                    color="primary"
                    variant="outlined"
                  />
                ))}
              </Box>
            </CardContent>
          </Card>
        </Grid>

        {/* Automatic Escalation Rules */}
        <Grid item xs={12} md={6}>
          <Card sx={{ height: "100%" }}>
            <CardContent>
              <Typography variant="subtitle1" fontWeight="bold" gutterBottom>
                Automatic Escalation
              </Typography>

              <Box sx={{ mb: 3 }}>
                <FormControlLabel
                  control={
                    <Switch
                      checked={formData.escalateOnNoMatch}
                      onChange={handleChange("escalateOnNoMatch")}
                    />
                  }
                  label="Escalate when no relevant answer found"
                />
                <Typography
                  variant="caption"
                  color="textSecondary"
                  display="block"
                >
                  Transfer to agent if the bot can't find relevant knowledge
                  base content
                </Typography>
              </Box>

              <Box>
                <Typography variant="body2" gutterBottom>
                  Max bot turns before suggesting agent:{" "}
                  {formData.escalateAfterTurns}
                </Typography>
                <Slider
                  value={formData.escalateAfterTurns}
                  onChange={handleSliderChange("escalateAfterTurns")}
                  step={1}
                  min={3}
                  max={15}
                  marks={[
                    { value: 3, label: "3" },
                    { value: 10, label: "10" },
                    { value: 15, label: "15" },
                  ]}
                />
                <Typography variant="caption" color="textSecondary">
                  After this many exchanges, suggest connecting to a human
                </Typography>
              </Box>
            </CardContent>
          </Card>
        </Grid>

        {/* Sentiment-Based Escalation */}
        <Grid item xs={12} md={6}>
          <Card sx={{ height: "100%" }}>
            <CardContent>
              <Typography variant="subtitle1" fontWeight="bold" gutterBottom>
                Sentiment-Based Escalation
              </Typography>

              <Box sx={{ mb: 3 }}>
                <FormControlLabel
                  control={
                    <Switch
                      checked={formData.autoHandoffOnNegativeSentiment}
                      onChange={handleChange("autoHandoffOnNegativeSentiment")}
                    />
                  }
                  label="Auto-escalate on negative sentiment"
                />
                <Typography
                  variant="caption"
                  color="textSecondary"
                  display="block"
                >
                  Automatically offer agent connection when detecting frustrated
                  customers
                </Typography>
              </Box>

              <Box>
                <Typography variant="body2" gutterBottom>
                  Sentiment threshold: {formData.sentimentThreshold}
                </Typography>
                <Slider
                  value={formData.sentimentThreshold}
                  onChange={handleSliderChange("sentimentThreshold")}
                  step={0.1}
                  min={-1}
                  max={0}
                  disabled={!formData.autoHandoffOnNegativeSentiment}
                  marks={[
                    { value: -1, label: "Very Negative" },
                    { value: -0.5, label: "Negative" },
                    { value: 0, label: "Neutral" },
                  ]}
                />
                <Typography variant="caption" color="textSecondary">
                  Escalate when sentiment score drops below this threshold
                </Typography>
              </Box>
            </CardContent>
          </Card>
        </Grid>

        {/* Save Button */}
        <Grid item xs={12}>
          <Divider sx={{ my: 2 }} />
          <Box display="flex" justifyContent="flex-end">
            <Button
              variant="contained"
              color="primary"
              startIcon={
                saving ? (
                  <CircularProgress size={20} color="inherit" />
                ) : (
                  <SaveIcon />
                )
              }
              onClick={handleSave}
              disabled={saving || !hasChanges}
            >
              {saving ? "Saving..." : "Save Escalation Rules"}
            </Button>
          </Box>
        </Grid>
      </Grid>
    </Box>
  );
}

export default EscalationConfigPanel;
